// Shared types + palette for the Manpower Cost Analysis report.
// Manpower cost is dual-source: 'PRW' = applied PRW manpower norms
// (manpower_norm_task_applications, grain = manpowerNormId) and 'OTHERS' =
// task resources (grain = laborTypeId + laborCategoryId + workUomId). There is
// no single id across sources, so row identity = source + conditional ids.
// All decimals arrive as strings and gRPC may omit zero-valued fields, so every
// reader goes through `num` and defaults missing values to 0.

// `compareGroupLabels` is imported (used below by buildGroupFamilyMap) as well
// as re-exported, so this import must sit above the `export … from` lines —
// those count as module body, and eslint's import/first fires otherwise.
import { compareGroupLabels, groupOf } from "../shared/normGroupLabels";

export {
  num,
  fmtINR,
  fmtINRCompact,
  fmtQty,
  computeParetoAbc,
} from "../ThreeMConsolidation/threeMShared";

export { compareGroupLabels } from "../shared/normGroupLabels";

export type ManpowerSource = "PRW" | "OTHERS";

export interface ManpowerSummaryRow {
  source: ManpowerSource;
  manpowerNormId?: number;
  laborTypeId?: number;
  laborCategoryId?: number;
  workUomId?: number;
  itemName?: string;
  itemCode?: string;
  uomName?: string;
  totalQuantity?: string;
  avgRate?: string;
  totalCost?: string;
  taskCount?: number;
  lineCount?: number;
  // Norm group, resolved server-side from this row's own BOQ code (itemCode).
  // Absent on every OTHERS row — task resources are labour types, not norms —
  // and on any PRW norm whose code the project's dictionary does not know.
  level1Code?: string; // 'B3'
  level1Name?: string; // 'Underground Excavation'
  groupCode?: string; // 'B3.2', or 'B3.3(1)' where the BOQ discriminates
  groupName?: string; // 'Excavation of all Hydraulic tunnels'
}

export interface ManpowerSummaryData {
  projectName?: string;
  totalCost?: string;
  prwCost?: string;
  othersCost?: string;
  itemCount?: number;
  items?: ManpowerSummaryRow[];
}

// Source family colours: PRW = blue, Others = orange (matches the PRW/Others
// tags used across the task drawers).
export const SOURCE_COLORS: Record<ManpowerSource, string> = {
  PRW: "#1677ff",
  OTHERS: "#fa8c16",
};

// Shade ramps for the donut's outer ring — item sub-slices stay inside their
// source's colour family so the ring reads as "parts of PRW / parts of Others".
export const PRW_SHADES = ["#0958d9", "#1677ff", "#4096ff", "#69b1ff", "#91caff"];
export const OTHERS_SHADES = ["#d46b08", "#fa8c16", "#ffa940", "#ffc069", "#ffd591"];

// Norm-group families. `base` paints a group itself (the donut's inner ring, a
// group row's % Share bar); `leaf` paints one of its items; `shades` is the ramp
// the donut's outer ring walks so an item slice reads as part of its group.
//
// SIX hues, not more, and the count is the whole point. These were picked by
// searching the antd ramps for the set with the greatest minimum separation,
// measured in OKLab (x100) with Vienot dichromat simulation:
//
//     min separation, ALL pairs, normal vision : 22.0   (hard floor is 15)
//     min separation, ADJACENT pairs, CVD      : 17.5   (target is 8)
//
// An eighth hue cannot be added without breaking that. The best 8-hue set from
// the same ramps falls to 7.8 adjacent-CVD, and the previous ad-hoc 8-hue list
// was worse still: blue #1677ff vs geekblue #2f54eb measured **8.3 apart in
// NORMAL vision** and orange #fa8c16 vs gold #faad14 just **7.7** — pairs that
// full-colour readers cannot tell apart, which no amount of labelling excuses.
// So a seventh group folds into NEUTRAL_FAMILY rather than getting a generated
// hue. If you are tempted to add one, re-run the search first.
//
// Known and accepted: the base->leaf step is much smaller on gold and cyan
// (dE ~4) than on blue/red/purple/green (dE ~16), because those two hues are
// already light and there is nowhere lighter to go that stays visible against
// the row. It is cosmetic — a group row is marked by its indentation, bold
// label, caret and row background, not by out-contrasting its own items — and
// closing it would mean widening the lightness band and re-validating the six.
// Every leaf still clears its track by dE >= 16.
export const GROUP_FAMILIES: Array<{
  base: string;
  leaf: string;
  shades: string[];
}> = [
  { base: "#1677ff", leaf: "#69b1ff", shades: ["#0958d9", "#1677ff", "#4096ff", "#69b1ff", "#91caff"] },
  { base: "#f5222d", leaf: "#ff7875", shades: ["#cf1322", "#f5222d", "#ff4d4f", "#ff7875", "#ffa39e"] },
  { base: "#531dab", leaf: "#9254de", shades: ["#391085", "#531dab", "#722ed1", "#9254de", "#b37feb"] },
  { base: "#ffc53d", leaf: "#ffd666", shades: ["#d48806", "#faad14", "#ffc53d", "#ffd666", "#ffe58f"] },
  { base: "#389e0d", leaf: "#73d13d", shades: ["#237804", "#389e0d", "#52c41a", "#73d13d", "#95de64"] },
  { base: "#36cfc9", leaf: "#5cdbd3", shades: ["#08979c", "#13c2c2", "#36cfc9", "#5cdbd3", "#87e8de"] },
];

// Fallback bucket for a row the norm dictionary could not place. Kept verbatim
// in step with the 3M Deviation Summary's manpower tab (TAB_META.MANPOWER) so
// the two pages bucket the same rows under the same headings.
const sourceFallback = (source?: ManpowerSource): string =>
  source === "PRW" ? "PRW / Sub-Contract" : "Others (Task Resources)";

/** Top grouping level: the BOQ head, e.g. `B3 — Underground Excavation`. */
export const manpowerGroupLabel = (row: ManpowerSummaryRow): string =>
  groupOf(row.level1Code, row.level1Name) ?? sourceFallback(row.source);

/**
 * Second level, nested under the first. Null when the row has no group — the
 * caller then keeps it directly under the level-1 heading rather than inventing
 * a sub-group.
 */
export const manpowerSubGroupLabel = (row: ManpowerSummaryRow): string | null =>
  groupOf(row.groupCode, row.groupName);

// Groups past the end of the palette share one neutral family rather than
// getting a generated hue — a made-up 9th colour reads as a 9th meaning.
export const NEUTRAL_FAMILY = {
  base: "#8c9bb0",
  leaf: "#c2ccd9",
  shades: ["#5c6b80", "#8c9bb0", "#a6b3c4", "#c2ccd9", "#dde3ea"],
};

/**
 * Stable colour family per norm group, shared by the register's % Share bars
 * and the donut's Norm Group ring so one group is one colour across the page.
 *
 * Keyed on the group's OWN identity in BOQ order — never on its cost rank, and
 * never on the filtered subset. That is what stops a source filter or a search
 * from repainting the groups that survive it: colour belongs to the entity, so
 * removing B4 must not turn B9 into B4's colour.
 */
export const buildGroupFamilyMap = (
  items: ManpowerSummaryRow[]
): Map<string, number> => {
  const labels = Array.from(
    new Set((items || []).map(manpowerGroupLabel))
  ).sort(compareGroupLabels);
  const map = new Map<string, number>();
  labels.forEach((label, index) =>
    map.set(label, index < GROUP_FAMILIES.length ? index : -1)
  );
  return map;
};

/** The palette entry for a family index, with the neutral fallback. */
export const groupFamily = (index: number | undefined) =>
  index === undefined || index < 0 ? NEUTRAL_FAMILY : GROUP_FAMILIES[index];

// Stable identity for table keys and the breakdown drawer. gRPC omits
// zero-valued fields, hence the ?? 0 fallbacks.
export const manpowerRowKey = (row: ManpowerSummaryRow): string =>
  row.source === "PRW"
    ? `PRW-${row.manpowerNormId ?? 0}`
    : `OTH-${row.laborTypeId ?? 0}-${row.laborCategoryId ?? 0}-${row.workUomId ?? 0}`;

// One flat row of the all-items task breakdown (Excel export): the per-item
// drill-down rows plus the summary identity (ids arrive as 0 for NULL, so
// manpowerRowKey works on these rows too), straight from
// GET 3m-consolidation/manpower-breakdown-all/:projectId.
export interface ManpowerBreakdownAllRow {
  source: ManpowerSource;
  manpowerNormId?: number;
  laborTypeId?: number;
  laborCategoryId?: number;
  workUomId?: number;
  itemName?: string;
  itemCode?: string;
  uomName?: string;
  phaseId?: number;
  phaseName?: string;
  activityId?: number;
  activityName?: string;
  taskId?: number;
  taskName?: string;
  taskSequenceId?: string;
  totalQuantity?: string;
  totalCost?: string;
  lineCount?: number;
}

// One (labour item, month) cell of the all-items month grid, from
// GET 3m-consolidation/manpower-monthly-all/:projectId. Ids arrive as 0 for
// NULL, so manpowerRowKey works on these rows too. `head` is the source —
// manpower has no category column of its own.
export interface ManpowerMonthCellRow {
  source: ManpowerSource;
  head?: string;
  manpowerNormId?: number;
  laborTypeId?: number;
  laborCategoryId?: number;
  workUomId?: number;
  month: string;
  monthQuantity?: string;
  monthCost?: string;
}
